/**
 * Import Summary Component
 * 
 * Shows final import results with rollback options and success metrics
 * Displays imported assets and provides navigation options
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Eye, RotateCcw, FileText, ArrowRight, Clock } from 'lucide-react';
import { canRollback, rollbackImport, getImportSummary } from './utils/importHistory';

const ImportSummary = ({ 
  importResult, 
  onViewAssets, 
  onStartNewImport, 
  onClose,
  disabled = false 
}) => {
  const [rollbackAvailable, setRollbackAvailable] = useState(false);
  const [rollbackTimeRemaining, setRollbackTimeRemaining] = useState(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [rollbackResult, setRollbackResult] = useState(null);

  useEffect(() => {
    if (importResult.importId) {
      setRollbackAvailable(canRollback(importResult.importId));
      
      // Update rollback timer every second
      const interval = setInterval(() => {
        const stillAvailable = canRollback(importResult.importId);
        setRollbackAvailable(stillAvailable);
        
        if (stillAvailable) {
          const summary = getImportSummary(importResult.importId);
          if (summary && summary.rollbackExpiresAt) {
            const expiry = new Date(summary.rollbackExpiresAt);
            const now = new Date();
            const timeLeft = Math.max(0, Math.floor((expiry - now) / 1000));
            setRollbackTimeRemaining(timeLeft);
          }
        } else {
          setRollbackTimeRemaining(null);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [importResult.importId]);

  const handleRollback = async () => {
    if (!importResult.importId || !rollbackAvailable || isRollingBack) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to rollback this import? This will remove all ${importResult.successfulRows} imported assets and cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setIsRollingBack(true);
    try {
      const result = rollbackImport(importResult.importId);
      setRollbackResult(result);
      setRollbackAvailable(false);
      
      if (result.success) {
        // Refresh the page or trigger a data refresh after successful rollback
        setTimeout(() => {
          if (onViewAssets) {
            onViewAssets(); // This should refresh the asset list
          }
        }, 2000);
      }
    } catch (error) {
      setRollbackResult({
        success: false,
        error: `Rollback failed: ${error.message}`
      });
    } finally {
      setIsRollingBack(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getStatusIcon = () => {
    if (importResult.failedRows === 0) {
      return <CheckCircle className="h-8 w-8 text-green-500" />;
    }
    return <AlertCircle className="h-8 w-8 text-yellow-500" />;
  };

  const getStatusMessage = () => {
    if (importResult.failedRows === 0) {
      return {
        title: 'Import Completed Successfully!',
        message: `All ${importResult.successfulRows} assets were imported without errors.`,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    }
    return {
      title: 'Import Completed with Issues',
      message: `${importResult.successfulRows} assets imported successfully, ${importResult.failedRows} rows failed.`,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    };
  };

  const status = getStatusMessage();

  return (
    <div className="space-y-6">
      {/* Rollback Result */}
      {rollbackResult && (
        <div className={`border rounded-lg p-4 ${
          rollbackResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center space-x-3">
            {rollbackResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-500" />
            )}
            <div>
              <h4 className={`font-medium ${
                rollbackResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {rollbackResult.success ? 'Rollback Successful' : 'Rollback Failed'}
              </h4>
              <p className={`text-sm mt-1 ${
                rollbackResult.success ? 'text-green-700' : 'text-red-700'
              }`}>
                {rollbackResult.message || rollbackResult.error}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Import Summary */}
      <div className={`border rounded-lg p-6 ${status.bgColor} ${status.borderColor}`}>
        <div className="flex items-start space-x-4">
          {getStatusIcon()}
          <div className="flex-1">
            <h3 className={`text-lg font-medium ${status.color}`}>{status.title}</h3>
            <p className={`text-sm mt-1 ${status.color}`}>{status.message}</p>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white rounded border">
                <div className="text-2xl font-bold text-blue-600">{importResult.totalRows}</div>
                <div className="text-sm text-gray-600">Total Rows</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                <div className="text-2xl font-bold text-green-600">{importResult.successfulRows}</div>
                <div className="text-sm text-gray-600">Imported</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded border border-red-200">
                <div className="text-2xl font-bold text-red-600">{importResult.failedRows}</div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded border">
                <div className="text-2xl font-bold text-gray-600">
                  {Math.round((importResult.successfulRows / importResult.totalRows) * 100)}%
                </div>
                <div className="text-sm text-gray-600">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rollback Option */}
      {rollbackAvailable && !rollbackResult && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <RotateCcw className="h-6 w-6 text-yellow-500 mt-1" />
            <div className="flex-1">
              <h4 className="text-lg font-medium text-yellow-800">Rollback Available</h4>
              <p className="text-sm text-yellow-700 mt-1">
                You can undo this import and remove all imported assets within the next{' '}
                <span className="font-medium">{formatTime(rollbackTimeRemaining)}</span>.
              </p>
              <div className="mt-4 flex items-center space-x-4">
                <button
                  onClick={handleRollback}
                  disabled={isRollingBack || disabled}
                  className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRollingBack ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      <span>Rolling back...</span>
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4" />
                      <span>Rollback Import</span>
                    </>
                  )}
                </button>
                {rollbackTimeRemaining && (
                  <div className="flex items-center space-x-2 text-sm text-yellow-700">
                    <Clock className="h-4 w-4" />
                    <span>Expires in {formatTime(rollbackTimeRemaining)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Details */}
      <div className="bg-white border rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Import Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">File name:</span>
              <span className="text-sm text-gray-900">{importResult.fileName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">File type:</span>
              <span className="text-sm text-gray-900">{importResult.fileType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Client:</span>
              <span className="text-sm text-gray-900">{importResult.clientName}</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Import time:</span>
              <span className="text-sm text-gray-900">
                {new Date(importResult.timestamp).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Processing time:</span>
              <span className="text-sm text-gray-900">{importResult.processingTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Import ID:</span>
              <span className="text-sm text-gray-900 font-mono text-xs">
                {importResult.importId}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Imported Assets Preview */}
      {importResult.importedAssets && importResult.importedAssets.length > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Imported Assets (Preview)</h4>
          <div className="space-y-2">
            {importResult.importedAssets.slice(0, 5).map((asset, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                <div>
                  <div className="font-medium text-gray-900">{asset.name}</div>
                  <div className="text-sm text-gray-600">{asset.ownerDepartment}</div>
                </div>
                <div className="text-sm text-gray-500">
                  {asset.criticalityClassification}
                </div>
              </div>
            ))}
            {importResult.importedAssets.length > 5 && (
              <p className="text-sm text-gray-600 text-center pt-2">
                ... and {importResult.importedAssets.length - 5} more assets
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error Summary */}
      {importResult.errors && importResult.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h4 className="text-lg font-medium text-red-800 mb-4">Import Errors</h4>
          <div className="space-y-2">
            {importResult.errors.slice(0, 3).map((error, index) => (
              <div key={index} className="text-sm text-red-700">
                Row {error.row}: {error.message}
              </div>
            ))}
            {importResult.errors.length > 3 && (
              <p className="text-sm text-red-600">
                ... and {importResult.errors.length - 3} more errors
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onClose}
          disabled={disabled}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Close
        </button>
        
        <div className="flex space-x-3">
          <button
            onClick={onStartNewImport}
            disabled={disabled}
            className="flex items-center space-x-2 px-6 py-3 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="h-4 w-4" />
            <span>Import Another File</span>
          </button>
          <button
            onClick={onViewAssets}
            disabled={disabled}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Eye className="h-4 w-4" />
            <span>View Imported Assets</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportSummary; 